import { Form, Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import { UserAnalyticsService } from "@analytics/index";
import { CampaignAnalyticsService } from "@analytics/index";

import TextField from "@components/Forms/FormInputs/TextField";
import RemoveFromNylasButton from "@components/Sidebar/RemoveFromNylasButton";
import SendFromFeature from "@components/Sidebar/SendFromFeature";

import { IUser } from "@interfaces/user.type";

import ConnectEmailAccountForm from "@pages/CreateCampaign/components/ConnectEmailAccountForm";

import userService from "@services/user.service";
import UserService from "@services/user.service";

import { usePopupActions } from "@store/popupStore";

interface IAccountModalProperties {
  onSubmit: (id: string, values: IUser) => Promise<void>;
  user: IUser;
}

const valueSchema = () =>
  Yup.object().shape({
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
  });

const proxySchema = () =>
  Yup.object().shape({
    sendEmailFrom: Yup.string().email("Invalid email address"),
  });

/**
 * Modal for updating user account details.
 * @param onSubmit - Function to call when the form is submitted.
 * @param user - The user object.
 */
export default function AccountModal({ onSubmit, user }: IAccountModalProperties) {
  const [nylasAuthenticated, setNylasAuthenticated] = useState(user.nylasAuthenticated);

  const PopupActions = usePopupActions();
  useEffect(() => {
    setNylasAuthenticated(user.nylasAuthenticated);
  }, [user.nylasAuthenticated]);

  const updateUser = async (values: IUser) => {
    if (user.id) {
      await onSubmit(user.id, values);
    }
  };

  const removeNylasFromUser = async () => {
    if (user.id) {
      await userService.update(user.id, { nylasAuthenticated: false, nylasToken: null });
      setNylasAuthenticated(false);
    }
  };

  const authenticateEmail = useCallback(() => {
    PopupActions.openModal({
      content: <ConnectEmailAccountForm />,
      title: "Connect Your Email Account",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    UserService.signOut();
    PopupActions.closeModal();
  };

  const accordionStyles = {
    "&.MuiAccordion-root:before": {
      backgroundColor: "grey.400",
    },
    "&.MuiAccordion-root.Mui-expanded:before": {
      opacity: 1,
    },
    bgcolor: "grey.50",
    py: 2,
  };

  return (
    <>
      <Accordion sx={{ ...accordionStyles }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <strong>User Details</strong>
        </AccordionSummary>
        <AccordionDetails>
          <Formik
            initialValues={{
              firstName: user?.firstName || "",
              jobTitle: user?.jobTitle || "",
              lastName: user?.lastName || "",
              organizationName: user?.organizationName || "",
            }}
            onSubmit={updateUser}
            validationSchema={valueSchema()}
          >
            {({ isSubmitting, isValid }) => (
              <Box component={Form} sx={{ width: "100%" }}>
                <TextField isDisabled={isSubmitting} isFullWidth label="First Name" name="firstName" type="text" />
                <TextField isDisabled={isSubmitting} isFullWidth label="Last Name" name="lastName" type="text" />
                <TextField isDisabled={isSubmitting} isFullWidth label="Job Title" name="jobTitle" type="text" />
                {/* <TextField
                  isDisabled={isSubmitting}
                  isFullWidth
                  label="Organization Name"
                  name="organizationName"
                  type="text"
                /> */}
                <br />
                <Button disabled={!isValid || isSubmitting} sx={{ mt: 3 }} type="submit" variant="contained">
                  {isSubmitting ? <CircularProgress size={20} /> : "Update Account Details"}
                </Button>
              </Box>
            )}
          </Formik>
        </AccordionDetails>
      </Accordion>
      <Accordion sx={{ ...accordionStyles }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <strong>Campaign Email</strong>
        </AccordionSummary>
        <AccordionDetails>
          {nylasAuthenticated ? (
            (user.features || []).includes("from_poc_feature") ? (
              <SendFromFeature
                proxySchema={proxySchema}
                removeNylasFromUser={removeNylasFromUser}
                sendEmailFrom={user.sendEmailFrom || null}
                updateUserSendEmailFrom={updateUser}
              />
            ) : (
              <RemoveFromNylasButton removeNylasFromUser={removeNylasFromUser} />
            )
          ) : (
            <Button onClick={authenticateEmail} variant="contained">
              Connect Email Account
            </Button>
          )}
        </AccordionDetails>
      </Accordion>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button color="secondary" onClick={handleLogout} variant="contained">
          Log Out
        </Button>
      </Box>
    </>
  );
}
